/**
 * Canvas Context Menu
 * Appears on right-click on the empty canvas area.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useWorkflowStore } from '@flow-studio/store';
import { useUIStore } from '@flow-studio/store';
import { getCanvasMenuBounds, clampMenuPosition } from '../../utils/canvasMenuBounds';

interface CanvasContextMenuProps {
  position: { x: number; y: number };
  onClose: () => void;
  onFitView: () => void;
  onSelectAll: () => void;
  /** Current canvas zoom level — scales the menu to match, same as NodeContextMenu. */
  zoom?: number;
}

export const CanvasContextMenu: React.FC<CanvasContextMenuProps> = ({
  position,
  onClose,
  onFitView,
  onSelectAll,
  zoom = 1,
}) => {
  const { clipboard } = useUIStore();
  const { nodes } = useWorkflowStore();
  const { theme } = useUIStore();
  const menuRef = useRef<HTMLDivElement>(null);
  // null until the first layout measurement — menu renders invisible (but measurable) until then.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  // Clamp the menu to stay fully inside the visible canvas — it's anchored to the
  // right-click cursor position, which is often near the canvas edge.
  useLayoutEffect(() => {
    const menuEl = menuRef.current;
    if (!menuEl) return;
    const menuRect = menuEl.getBoundingClientRect();
    const bounds = getCanvasMenuBounds(menuEl);
    setPos(clampMenuPosition({ top: position.y, left: position.x }, menuRect, bounds));
  }, [position.x, position.y, zoom]);

  const isDark = theme === 'dark';

  const menuStyle: React.CSSProperties = {
    position: 'fixed',
    left: pos ? `${pos.left}px` : `${position.x}px`,
    top: pos ? `${pos.top}px` : `${position.y}px`,
    visibility: pos ? 'visible' : 'hidden',
    transform: `scale(${zoom})`,
    transformOrigin: 'top left',
    background: isDark ? 'rgba(15, 23, 42, 0.97)' : 'rgba(255, 255, 255, 0.97)',
    backdropFilter: 'blur(12px)',
    border: isDark ? '1px solid rgba(58, 140, 69, 0.3)' : '1px solid #e5e7eb',
    borderRadius: '10px',
    boxShadow: isDark
      ? '0 0 20px rgba(58, 140, 69, 0.25), 0 10px 30px rgba(0,0,0,0.7)'
      : '0 4px 16px rgba(0,0,0,0.12)',
    minWidth: '190px',
    zIndex: 1000,
    overflow: 'hidden',
    padding: '4px 0',
  };

  const itemStyle: React.CSSProperties = {
    padding: '9px 16px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    color: isDark ? '#f1f5f9' : '#111827',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    userSelect: 'none',
  };

  const disabledItemStyle: React.CSSProperties = {
    ...itemStyle,
    opacity: 0.38,
    cursor: 'default',
  };

  const hoverBg = isDark ? 'rgba(58,140,69,0.15)' : '#f3f4f6';

  const Item: React.FC<{
    label: string;
    shortcut?: string;
    icon: React.ReactNode;
    disabled?: boolean;
    onClick?: () => void;
  }> = ({ label, shortcut, icon, disabled, onClick }) => (
    <div
      style={disabled ? disabledItemStyle : itemStyle}
      onMouseEnter={e => { if (!disabled) (e.currentTarget as HTMLElement).style.background = hoverBg; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      onClick={() => { if (!disabled && onClick) { onClick(); onClose(); } }}
    >
      <span style={{ width: 16, display: 'flex', justifyContent: 'center' }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {shortcut && (
        <span style={{ fontSize: '11px', opacity: 0.5, marginLeft: 8 }}>{shortcut}</span>
      )}
    </div>
  );

  const Separator = () => (
    <div style={{ height: '1px', background: isDark ? 'rgba(255,255,255,0.07)' : '#e5e7eb', margin: '3px 0' }} />
  );

  const hasClipboard = !!clipboard && (clipboard.nodes?.length ?? 0) > 0;
  const hasNodes = nodes.length > 0;

  return createPortal(
    <div ref={menuRef} style={menuStyle}>
      <Item
        label="Paste"
        shortcut="Ctrl+V"
        disabled={!hasClipboard}
        icon={
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
            <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
          </svg>
        }
        onClick={() => {
          // Trigger paste via keyboard event so existing shortcut handler fires
          document.dispatchEvent(new KeyboardEvent('keydown', { key: 'v', ctrlKey: true, bubbles: true }));
        }}
      />
      <Item
        label="Select All"
        shortcut="Ctrl+A"
        disabled={!hasNodes}
        icon={
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <path d="M9 9h6v6H9z"/>
          </svg>
        }
        onClick={onSelectAll}
      />
      <Separator />
      <Item
        label="Fit View"
        shortcut="Ctrl+⇧F"
        disabled={!hasNodes}
        icon={
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 3 21 3 21 9"/>
            <polyline points="9 21 3 21 3 15"/>
            <line x1="21" y1="3" x2="14" y2="10"/>
            <line x1="3" y1="21" x2="10" y2="14"/>
          </svg>
        }
        onClick={onFitView}
      />
    </div>,
    document.body
  );
};
